// src/views/words-lists/FolderTreeView.tsx
/* eslint-disable max-len */
import React, {
  FC, useRef, useCallback, useState, useEffect, useMemo,
} from 'react';
import {
  TreeApi, NodeApi, NodeRendererProps,
} from 'react-arborist';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Modal, message, Spin, Menu,
} from 'antd';
import {
  LoadingOutlined,
  FolderOutlined,
  FileTextOutlined,
  EditOutlined,
  ShareAltOutlined,
  DeleteOutlined,
  CopyOutlined,
  DragOutlined,
} from '@ant-design/icons';

// Components
import useMeasure from 'react-use-measure';
import { TreeNode } from './components/TreeNode';
import { InlineEdit } from './components/InlineEdit';
import { Toolbar } from './components/Toolbar';
import { CreateFolderModal } from './components/CreateFolderModal';
import { CreateListModal } from './components/CreateListModal';
import { MoveItemModal } from './components/MoveItemModal';
import { TreeContainer } from './components/TreeContainer';
import { BreadcrumbNav } from './components/BreadcrumbNav';
import { getListOfWordsList } from './slice/selectors';

// hooks
import { useTreeOperations } from './hooks/useTreeOperations';
import { useTreeSearch } from './hooks/useTreeSearch';
import { useBulkSelection } from './hooks/useBulkSelection';

// Redux
import {
  fetchFolderTreeWithLists,
  createFolder,
  deleteFolder,
  setSelectedItem,
  renameItem,
  toggleFolder,
  expandAllFolders,
  collapseAllFolders,
  moveItem,
  invalidateCache,
} from '../folders/slice/actions';
import { createWordsListInFolder, deleteWordsList, duplicateWordsList } from './slice/actions';
import {
  getFolderTreeState,
  getOperationsState,
} from '../folders/slice/selectors';
import { getActiveList } from '../auth/slice/selectors';
import { getWordsListLink } from '../../constants/Path';
import { setActiveList } from '../auth/slice/actions';

import styles from './FolderTreeView.module.less';
import { BreadcrumbItemType, TreeNodeType } from '../../services/ApiService';
import ShareWordsListModal from './modals/ShareWordsListModal';

// ✅ USUNIĘTY globalny cache - był główną przyczyną problemów!

// Okno „Duplikuj do…” pokazuje wszystkie foldery
const NO_EXCLUDED_NODES = new Set<string>();

// Folder, w którym leży element drzewa; `null` to katalog główny
const getParentFolderId = (node: NodeApi<TreeNodeType>): number | null => (
  node.parent && !node.parent.isRoot ? parseInt(node.parent.id.replace('folder-', ''), 10) : null
);

interface FolderTreeViewProps {
  isVisible?: boolean;
}

const FolderTreeView: FC<FolderTreeViewProps> = ({ isVisible = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const treeRef = useRef<TreeApi<TreeNodeType>>(null);

  const isMountedRef = useRef(true);
  const hasInitiallyFetchedRef = useRef(false);
  const treeKey = 'stable-folder-tree';

  const treeState = useSelector(getFolderTreeState, shallowEqual);
  const operationsState = useSelector(getOperationsState, shallowEqual);
  const activeList = useSelector(getActiveList, shallowEqual);

  // STATE dla globalnego context menu
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    node: NodeApi<TreeNodeType> | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    node: null,
  });
  const contextMenuRef = useRef<HTMLDivElement>(null);

  const [moveModal, setMoveModal] = useState<{
    visible: boolean;
    node: NodeApi<TreeNodeType> | null;
  }>({
    visible: false,
    node: null,
  });

  // Lista, dla której otwarte jest okno „Duplikuj do…”
  const [duplicateNode, setDuplicateNode] = useState<NodeApi<TreeNodeType> | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const reduxState = useMemo(() => ({
    folderTreeData: treeState.folderTreeData,
    expandedFolders: treeState.expandedFolders,
    isLoading: treeState.isLoading,
    isCreating: operationsState.isCreating,
    isMoving: operationsState.isMoving,
    isRenaming: operationsState.isRenaming,
    isDeleting: operationsState.isDeleting,
    activeList,
  }), [
    treeState.folderTreeData,
    treeState.expandedFolders,
    treeState.isLoading,
    operationsState.isCreating,
    operationsState.isMoving,
    operationsState.isRenaming,
    operationsState.isDeleting,
    activeList,
  ]);

  const renderCountRef = useRef<number>(0);
  renderCountRef.current += 1;

  const initialOpenState = useMemo(() => {
    const state: { [key: string]: boolean } = {};
    reduxState.expandedFolders.forEach((folderId: string) => {
      state[folderId] = true;
    });

    return state;
  }, []);

  // ✅ Używamy tylko Redux state, bez fallbacków na cache
  const effectiveTreeData = useMemo(() => reduxState.folderTreeData, [reduxState.folderTreeData]);

  // ✅ Uproszczona logika ładowania
  const shouldShowInitialLoading = useMemo(() => reduxState.isLoading && effectiveTreeData.length === 0 && !hasInitiallyFetchedRef.current, [reduxState.isLoading, effectiveTreeData.length]);

  // Custom hooks
  const {
    calculateNodeLevel,
    handleToggleActive,
    clearCache,
    MAX_NESTING_LEVEL,
  } = useTreeOperations();

  const {
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    filteredTree,
    resetSearch,
  } = useTreeSearch(
    effectiveTreeData,
    treeRef,
    { sortBy: 'createdAt', sortOrder: 'desc' },
  );

  const {
    selectedNodes,
    isBulkMode,
    setIsBulkMode,
    toggleBulkSelection,
    handleBulkDelete,
  } = useBulkSelection();

  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItemType[]>([{ id: 'root', name: '' }]);
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [sharedWordsList, setSharedWordsList] = useState<any>(null);
  const lists = useSelector(getListOfWordsList);

  const [containerRef, { height: containerHeight }] = useMeasure({
    debounce: 50,
  });

  const [createFolderModal, setCreateFolderModal] = useState({
    visible: false,
    parentId: null as string | null,
    name: '',
  });

  const [createListModal, setCreateListModal] = useState({
    visible: false,
    parentFolderId: null as number | null,
    name: '',
    description: '',
  });

  const collectDescendantIds = useCallback((node: TreeNodeType): Set<string> => {
    const ids = new Set<string>([node.id]);

    const collectChildren = (children: TreeNodeType[] | undefined) => {
      if (!children) return;
      children.forEach((child) => {
        ids.add(child.id);
        if (child.children) {
          collectChildren(child.children);
        }
      });
    };

    collectChildren(node.children);

    return ids;
  }, []);

  const handleOpenMoveModal = useCallback((node: NodeApi<TreeNodeType>) => {
    setMoveModal({
      visible: true,
      node,
    });
  }, []);

  // ✅ KLUCZ: Zawsze invaliduj cache przed przenoszeniem
  const handleMoveItem = useCallback(async (targetFolderId: number | null) => {
    if (!moveModal.node) return;

    const { node } = moveModal;
    const { type, id } = node.data;
    const itemId = parseInt(id.replace(`${type}-`, ''), 10);

    try {
      if (type === 'folder' && targetFolderId !== null) {
        const targetFolderNode = treeRef.current?.get(`folder-${targetFolderId}`);
        if (targetFolderNode) {
          const targetLevel = calculateNodeLevel(targetFolderNode);
          if (targetLevel >= MAX_NESTING_LEVEL - 1) {
            message.warning(t('folders:messages.maxNesting', { max: MAX_NESTING_LEVEL }));

            return;
          }
        }
      }

      // ✅ Invaliduj cache przed operacją
      dispatch(invalidateCache());

      dispatch(moveItem(itemId, type, targetFolderId, 0));

      if (!isMountedRef.current) return;

      message.success(t(type === 'folder' ? 'folders:messages.folderMoved' : 'folders:messages.listMoved'));

      setMoveModal({ visible: false, node: null });

      if (targetFolderId) {
        const folderId = `folder-${targetFolderId}`;
        if (!reduxState.expandedFolders.includes(folderId)) {
          dispatch(toggleFolder(folderId));
        }
      }
    } catch (error) {
      console.error('Move error:', error);
      message.error(t('folders:messages.moveError'));
    }
  }, [moveModal, dispatch, calculateNodeLevel, MAX_NESTING_LEVEL, reduxState.expandedFolders, t]);

  const handleContextMenu = useCallback((node: NodeApi<TreeNodeType>, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      node,
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, visible: false }));
  }, []);

  // Nasłuch w fazie przechwytywania — wiersze drzewa zatrzymują kliknięcie, zanim dotrze do dokumentu
  useEffect(() => {
    if (!contextMenu.visible) return undefined;

    const isOutsideMenu = (e: Event) => !contextMenuRef.current?.contains(e.target as Node);

    // Kliknięcie poza menu tylko je zamyka — nie rozwija folderu ani nie aktywuje listy
    const handleClick = (e: MouseEvent) => {
      if (!isOutsideMenu(e)) return;
      e.preventDefault();
      e.stopPropagation();
      handleCloseContextMenu();
    };

    // Prawy przycisk na innym wierszu od razu otworzy menu w nowym miejscu
    const handleRightClick = (e: MouseEvent) => {
      if (isOutsideMenu(e)) {
        handleCloseContextMenu();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseContextMenu();
      }
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('contextmenu', handleRightClick, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('contextmenu', handleRightClick, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu.visible, handleCloseContextMenu]);

  const findNodeById = useCallback((nodes: TreeNodeType[], id: string): TreeNodeType | null => {
    const queue = [...nodes];
    while (queue.length > 0) {
      const node = queue.shift()!;
      if (node.id === id) return node;
      if (node.children && node.children.length > 0) {
        queue.push(...node.children);
      }
    }

    return null;
  }, []);

  const findParentNode = useCallback((nodes: TreeNodeType[], childId: string): TreeNodeType | null => {
    const queue = [...nodes];
    while (queue.length > 0) {
      const node = queue.shift()!;
      if (node.children?.some((child) => child.id === childId)) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        queue.push(...node.children);
      }
    }

    return null;
  }, []);

  const buildBreadcrumbs = useCallback((nodeId: string | null) => {
    const path: BreadcrumbItemType[] = [{ id: 'root', name: '' }];
    if (!nodeId || nodeId === 'root') {
      setBreadcrumbs(path);

      return;
    }

    let currentNode = findNodeById(effectiveTreeData, nodeId);
    const tempPath: BreadcrumbItemType[] = [];

    while (currentNode) {
      tempPath.unshift({ id: currentNode.id, name: currentNode.name });
      const parent = findParentNode(effectiveTreeData, currentNode.id);
      currentNode = parent;
    }

    setBreadcrumbs([...path, ...tempPath]);
  }, [effectiveTreeData, findNodeById, findParentNode]);

  const handleNodeSelect = useCallback((nodes: NodeApi<TreeNodeType>[]) => {
    if (isBulkMode) {
      nodes.forEach((node) => toggleBulkSelection(node.id));
    } else {
      const selectedNode = nodes[0] ?? null;
      dispatch(setSelectedItem(selectedNode?.id ?? null));
      buildBreadcrumbs(selectedNode?.id ?? null);
    }
  }, [dispatch, isBulkMode, toggleBulkSelection, buildBreadcrumbs]);

  // ✅ KLUCZ: Invaliduj cache przed usuwaniem
  const handleDeleteItem = useCallback((node: NodeApi<TreeNodeType>) => {
    const isFolder = node.data.type === 'folder';
    const hasChildren = isFolder && node.children !== null && node.children.length > 0;
    const childrenCount = node.children?.length ?? 0;

    const getModalContent = () => {
      if (!isFolder) return t('folders:delete.listConfirm', { name: node.data.name });
      if (hasChildren) return t('folders:delete.folderWithChildren', { name: node.data.name, n: childrenCount });

      return t('folders:delete.folderConfirm', { name: node.data.name });
    };

    Modal.confirm({
      title: isFolder ? t('folders:delete.folderTitle') : t('folders:delete.listTitle'),
      content: getModalContent(),
      okText: t('common:buttons.delete'),
      cancelText: t('common:buttons.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!isMountedRef.current) return;

        const { id, type } = node.data;
        const itemId = parseInt(id.replace(`${type}-`, ''), 10);

        try {
          if (!isFolder && reduxState.activeList?.id === itemId) {
            dispatch(setActiveList(null));
          }

          // ✅ Invaliduj cache przed operacją
          dispatch(invalidateCache());

          // Drzewo odświeżane dopiero po usunięciu — wcześniej mogło wrócić z usuniętą jeszcze listą
          if (isFolder) {
            // deleteFolder sam odświeża drzewo
            await dispatch(deleteFolder(itemId));
          } else {
            await dispatch(deleteWordsList(itemId) as any);
            dispatch(fetchFolderTreeWithLists(true));
          }
          if (isMountedRef.current && editingNodeId === node.id) {
            setEditingNodeId(null);
          }
        } catch (error) {
          console.error('Delete error:', error);
          message.error(t('folders:messages.deleteError'));
        }
      },
    });
  }, [dispatch, editingNodeId, reduxState.activeList, t]);

  const validateFolderName = useCallback((name: string): string | null => {
    if (!name.trim()) return t('folders:validation.nameEmpty');
    if (name.length > 100) return t('folders:validation.nameTooLong', { max: 100 });
    if (!/^[a-zA-Z0-9ĄąĆćĘęŁłŃńÓóŚśŹźŻż\s\-_()]+$/.test(name)) {
      return t('folders:validation.nameInvalid');
    }

    return null;
  }, [t]);

  const validateListName = useCallback((name: string): string | null => {
    if (!name.trim()) return t('folders:validation.nameEmpty');
    if (name.length > 255) return t('folders:validation.nameTooLong', { max: 255 });

    return null;
  }, [t]);

  const handleCreateFolder = useCallback(async () => {
    const { name, parentId } = createFolderModal;

    const validationError = validateFolderName(name);
    if (validationError) {
      message.error(validationError);

      return;
    }

    const parentFolderId = parentId
      ? parseInt(parentId.replace('folder-', ''), 10)
      : undefined;

    try {
      // ✅ Invaliduj cache przed operacją
      dispatch(invalidateCache());

      await dispatch(createFolder(name, parentFolderId));

      if (!isMountedRef.current) return;

      if (isMountedRef.current) {
        setCreateFolderModal({ visible: false, parentId: null, name: '' });

        if (parentId && !reduxState.expandedFolders.includes(parentId)) {
          dispatch(toggleFolder(parentId));
        }
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      message.error(t('folders:messages.createFolderError'));
    }
  }, [dispatch, createFolderModal, reduxState.expandedFolders, validateFolderName, t]);

  const handleCreateList = useCallback(async () => {
    const { name, description, parentFolderId } = createListModal;

    const validationError = validateListName(name);
    if (validationError) {
      message.error(validationError);

      return;
    }

    try {
      // ✅ Invaliduj cache przed operacją
      dispatch(invalidateCache());

      await dispatch(createWordsListInFolder({
        name,
        description,
        folderId: parentFolderId || undefined,
      }) as any);

      if (!isMountedRef.current) return;

      // ✅ Wymuś odświeżenie
      dispatch(fetchFolderTreeWithLists(true));

      if (isMountedRef.current) {
        message.success(t('folders:messages.listCreated'));
        setCreateListModal({
          visible: false,
          parentFolderId: null,
          name: '',
          description: '',
        });

        if (parentFolderId) {
          const folderId = `folder-${parentFolderId}`;
          if (!reduxState.expandedFolders.includes(folderId)) {
            dispatch(toggleFolder(folderId));
          }
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        message.error(t('folders:messages.createListError'));
      }
    }
  }, [dispatch, createListModal, reduxState.expandedFolders, validateListName, t]);

  const handleShareList = useCallback((node: NodeApi<TreeNodeType>) => {
    if (node.data.type === 'list') {
      const listId = parseInt(node.data.id.replace('list-', ''), 10);
      const fullListData = lists.find((list: any) => list.id === listId);

      if (fullListData) {
        setSharedWordsList({
          id: fullListData.id,
          name: fullListData.name,
          sharedStudentIds: fullListData.sharedStudentIds || [],
        });
      } else {
        message.warning(t('folders:messages.listDataNotFound'));
      }
    }
  }, [lists, t]);

  const handleCreateSubfolder = useCallback((node: NodeApi<TreeNodeType>) => {
    const level = calculateNodeLevel(node);
    if (level >= MAX_NESTING_LEVEL - 1) {
      Modal.warning({
        title: t('folders:messages.nestingTitle'),
        content: t('folders:messages.nestingContent', { max: MAX_NESTING_LEVEL }),
        okText: 'OK',
      });

      return;
    }
    setCreateFolderModal({
      visible: true,
      parentId: node.data.id,
      name: '',
    });
  }, [calculateNodeLevel, MAX_NESTING_LEVEL, t]);

  const handleCreateListInFolder = useCallback((node: NodeApi<TreeNodeType>) => {
    const folderId = parseInt(node.data.id.replace('folder-', ''), 10);
    setCreateListModal({
      visible: true,
      parentFolderId: folderId,
      name: '',
      description: '',
    });
  }, []);

  const handleItemClick = useCallback((node: NodeApi<TreeNodeType>) => {
    if (node.data.type === 'list') {
      const listId = parseInt(node.data.id.replace('list-', ''), 10);
      history.push(getWordsListLink(listId));
    } else {
      // Redux dopasowuje się do drzewa: folder rozwinięty przez wyszukiwarkę nie jest w expandedFolders
      const isOpening = !node.isOpen;
      node.toggle();
      if (isOpening !== reduxState.expandedFolders.includes(node.data.id)) {
        dispatch(toggleFolder(node.data.id));
      }
    }

    if (!isBulkMode && treeRef.current && !node.isSelected) {
      treeRef.current.select(node.id);
    }
  }, [dispatch, isBulkMode, history, reduxState.expandedFolders]);

  const handleInlineRename = useCallback((nodeId: string, newName: string) => {
    dispatch(renameItem(nodeId, newName));
    setEditingNodeId(null);
  }, [dispatch]);

  const expandAll = useCallback(() => {
    dispatch(expandAllFolders());
    treeRef.current?.openAll();
  }, [dispatch]);

  const collapseAll = useCallback(() => {
    dispatch(collapseAllFolders());
    treeRef.current?.closeAll();
  }, [dispatch]);

  const isExpanded = useMemo(() => reduxState.expandedFolders.length > 0, [reduxState.expandedFolders]);

  const handleToggleExpand = useCallback(() => {
    if (isExpanded) {
      collapseAll();
    } else {
      expandAll();
    }
  }, [isExpanded, collapseAll, expandAll]);

  const handleBreadcrumbClick = useCallback((crumbId: string) => {
    if (crumbId === 'root') {
      dispatch(setSelectedItem(null));
      buildBreadcrumbs(null);
    } else {
      dispatch(setSelectedItem(crumbId));
      buildBreadcrumbs(crumbId);
      if (treeRef.current) {
        treeRef.current.select(crumbId);
      }
    }
  }, [dispatch, buildBreadcrumbs]);

  const handleDuplicateList = useCallback(async (targetFolderId: number | null) => {
    if (!duplicateNode || duplicateNode.data.type !== 'list') return;

    const listId = parseInt(duplicateNode.data.id.replace('list-', ''), 10);
    const sourceFolderId = getParentFolderId(duplicateNode);

    setIsDuplicating(true);

    // ✅ Invaliduj cache przed operacją
    dispatch(invalidateCache());

    const result = await dispatch(duplicateWordsList({ id: listId, sourceFolderId, targetFolderId }) as any);

    if (!isMountedRef.current) return;

    setIsDuplicating(false);

    // Okno zostaje otwarte — można spróbować jeszcze raz albo zrezygnować
    if (duplicateWordsList.rejected.match(result)) {
      console.error('Error duplicating list:', result.error);
      message.error(t('folders:messages.duplicateError'));

      return;
    }

    const { isInTargetFolder } = result.payload;

    if (isInTargetFolder) {
      message.success(t('folders:messages.copySaved'));
    } else {
      message.warning(t('folders:messages.copyNotMoved'));
    }

    setDuplicateNode(null);

    // ✅ Wymuś odświeżenie
    dispatch(fetchFolderTreeWithLists(true));

    // Cała ścieżka do kopii, nie tylko jej folder — inaczej kopia może zostać w zwiniętym folderze nadrzędnym
    const copyFolderId = isInTargetFolder ? targetFolderId : sourceFolderId;
    let folder: NodeApi<TreeNodeType> | null | undefined = copyFolderId === null ? null : treeRef.current?.get(`folder-${copyFolderId}`);
    while (folder && !folder.isRoot) {
      if (!reduxState.expandedFolders.includes(folder.id)) {
        dispatch(toggleFolder(folder.id));
      }
      folder = folder.parent;
    }
  }, [dispatch, duplicateNode, reduxState.expandedFolders, t]);

  const renderNode = useCallback((props: NodeRendererProps<TreeNodeType>) => {
    const { node } = props;
    const listId = node.data.type === 'list'
      ? parseInt(node.data.id.replace('list-', ''), 10)
      : null;
    const isActive = listId !== null && reduxState.activeList?.id === listId;
    const isBulkSelected = selectedNodes.has(node.id);

    if (editingNodeId === node.id) {
      return (
        <div style={props.style}>
          <InlineEdit
            value={node.data.name}
            onSave={(newName) => handleInlineRename(node.id, newName)}
            onCancel={() => setEditingNodeId(null)}
          />
        </div>
      );
    }

    return (
      <TreeNode
        {...props}
        isActive={isActive}
        isBulkMode={isBulkMode}
        isBulkSelected={isBulkSelected}
        searchQuery={debouncedSearchQuery}
        onItemClick={handleItemClick}
        onToggleActive={handleToggleActive}
        onToggleBulkSelection={toggleBulkSelection}
        onContextMenu={handleContextMenu}
      />
    );
  }, [reduxState.activeList?.id, selectedNodes, editingNodeId, isBulkMode, debouncedSearchQuery, handleItemClick, handleToggleActive, toggleBulkSelection, handleContextMenu, handleInlineRename]);

  // ✅ Uproszczony useEffect - fetch tylko raz przy montowaniu
  useEffect(() => {
    isMountedRef.current = true;

    if (!hasInitiallyFetchedRef.current) {
      hasInitiallyFetchedRef.current = true;
      dispatch(fetchFolderTreeWithLists());
    }

    return () => {
      isMountedRef.current = false;
      Modal.destroyAll();
    };
  }, [dispatch]);

  useEffect(() => {
    if (!treeRef.current) return;

    reduxState.expandedFolders.forEach((folderId: string) => {
      const node = treeRef.current?.get(folderId);
      if (node && !node.isOpen) {
        node.open();
      }
    });
  }, [reduxState.expandedFolders]);

  useEffect(() => {
    clearCache();
  }, [effectiveTreeData, clearCache]);

  const treeProps = useMemo(() => ({
    key: treeKey,
    data: filteredTree,
    initialOpenState,
    // Otwarte są tylko foldery z expandedFolders; domyślnie biblioteka rozwija wszystkie
    openByDefault: false,
    width: '100%',
    height: containerHeight > 100 ? containerHeight : 1600,
    indent: 24,
    rowHeight: 36,
    overscanCount: 50,
    disableDrag: true,
    disableDrop: true,
    onSelect: handleNodeSelect,
  }), [
    treeKey,
    filteredTree,
    containerHeight,
    handleNodeSelect,
    initialOpenState,
  ]);

  const toolbarProps = {
    onCreateFolder: () => setCreateFolderModal({
      visible: true,
      parentId: null,
      name: '',
    }),
    onCreateList: () => setCreateListModal({
      visible: true,
      parentFolderId: null,
      name: '',
      description: '',
    }),
    onToggleExpand: handleToggleExpand,
    isExpanded,
    isBulkMode,
    onToggleBulkMode: () => setIsBulkMode(!isBulkMode),
    onBulkDelete: handleBulkDelete,
    selectedCount: selectedNodes.size,
    searchQuery,
    onSearchChange: setSearchQuery,
    isCreating: reduxState.isCreating,
    isDeleting: reduxState.isDeleting,
  };

  const createFolderModalProps = {
    visible: createFolderModal.visible,
    parentId: createFolderModal.parentId,
    name: createFolderModal.name,
    isCreating: reduxState.isCreating,
    onNameChange: (name: string) => setCreateFolderModal((prev) => ({ ...prev, name })),
    onCancel: () => setCreateFolderModal({ visible: false, parentId: null, name: '' }),
    onSubmit: handleCreateFolder,
  };

  const createListModalProps = {
    visible: createListModal.visible,
    parentFolderId: createListModal.parentFolderId,
    name: createListModal.name,
    description: createListModal.description,
    onNameChange: (name: string) => setCreateListModal((prev) => ({ ...prev, name })),
    onDescriptionChange: (description: string) => setCreateListModal((prev) => ({ ...prev, description })),
    onCancel: () => setCreateListModal({
      visible: false,
      parentFolderId: null,
      name: '',
      description: '',
    }),
    onSubmit: handleCreateList,
  };

  const contextMenuCanAddSubfolder = useMemo(() => {
    if (!contextMenu.node || contextMenu.node.data.type !== 'folder') return false;
    const level = calculateNodeLevel(contextMenu.node);

    return level < MAX_NESTING_LEVEL - 1;
  }, [contextMenu.node, calculateNodeLevel, MAX_NESTING_LEVEL]);

  const contextMenuPosition = useMemo(() => {
    if (!contextMenu.visible || !contextMenu.node) {
      return { top: 0, left: 0 };
    }

    const isFolder = contextMenu.node.data.type === 'folder';
    const menuItemHeight = 32;
    const dividerHeight = 9;

    const itemsCount = isFolder
      ? (contextMenuCanAddSubfolder ? 2 : 1) + 3
      : 5;

    const dividersCount = 2;
    const menuPadding = 8;

    const estimatedHeight = (itemsCount * menuItemHeight) + (dividersCount * dividerHeight) + menuPadding;

    let top = contextMenu.y - (estimatedHeight / 2 + 50);

    if (top < 10) {
      top = 10;
    }

    if (top + estimatedHeight > window.innerHeight - 100) {
      top = window.innerHeight - estimatedHeight - 100;
    }

    const menuWidth = 200;
    let left = contextMenu.x;

    if (contextMenu.x + menuWidth > window.innerWidth) {
      left = window.innerWidth - menuWidth - 10;
    }

    return { top, left };
  }, [contextMenu, contextMenuCanAddSubfolder]);

  const excludedNodeIds = useMemo(() => {
    if (!moveModal.node) return new Set<string>();

    const nodeData = findNodeById(effectiveTreeData, moveModal.node.id);
    if (!nodeData) return new Set<string>();

    return collectDescendantIds(nodeData);
  }, [moveModal.node, effectiveTreeData, findNodeById, collectDescendantIds]);

  const globalContextMenu = contextMenu.visible && contextMenu.node ? (
    <div
      ref={contextMenuRef}
      role="presentation"
      className="ant-dropdown ant-dropdown-placement-bottomLeft"
      style={{
        position: 'fixed',
        left: contextMenuPosition.left,
        top: contextMenuPosition.top,
        zIndex: 1050,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Menu
        className="ant-dropdown-menu"
        selectable={false}
        mode="vertical"
      >
        {contextMenu.node.data.type === 'folder' ? (
          <>
            <Menu.Item
              key="new-folder"
              icon={<FolderOutlined />}
              onClick={() => {
                handleCreateSubfolder(contextMenu.node!);
                handleCloseContextMenu();
              }}
              disabled={!contextMenuCanAddSubfolder}
            >
              {t('folders:menu.newSubfolder')}
            </Menu.Item>
            <Menu.Item
              key="new-list"
              icon={<FileTextOutlined />}
              onClick={() => {
                handleCreateListInFolder(contextMenu.node!);
                handleCloseContextMenu();
              }}
            >
              {t('folders:menu.newList')}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              key="rename"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingNodeId(contextMenu.node!.id);
                handleCloseContextMenu();
              }}
            >
              {t('folders:menu.rename')}
            </Menu.Item>
            <Menu.Item
              key="move"
              icon={<DragOutlined />}
              onClick={() => {
                handleOpenMoveModal(contextMenu.node!);
                handleCloseContextMenu();
              }}
            >
              {t('folders:menu.move')}
            </Menu.Item>
          </>
        ) : (
          <>
            <Menu.Item
              key="share"
              icon={<ShareAltOutlined />}
              onClick={() => {
                handleShareList(contextMenu.node!);
                handleCloseContextMenu();
              }}
            >
              {t('folders:menu.share')}
            </Menu.Item>
            <Menu.Item
              key="edit"
              icon={<EditOutlined />}
              onClick={() => {
                handleItemClick(contextMenu.node!);
                handleCloseContextMenu();
              }}
            >
              {t('folders:menu.edit')}
            </Menu.Item>
            <Menu.Item
              key="duplicate"
              icon={<CopyOutlined />}
              onClick={() => {
                setDuplicateNode(contextMenu.node!);
                handleCloseContextMenu();
              }}
            >
              {t('folders:menu.duplicate')}
            </Menu.Item>
            <Menu.Item
              key="move"
              icon={<DragOutlined />}
              onClick={() => {
                handleOpenMoveModal(contextMenu.node!);
                handleCloseContextMenu();
              }}
            >
              {t('folders:menu.move')}
            </Menu.Item>
          </>
        )}
        <Menu.Divider />
        <Menu.Item
          key="delete"
          icon={<DeleteOutlined />}
          danger
          onClick={() => {
            handleDeleteItem(contextMenu.node!);
            handleCloseContextMenu();
          }}
        >
          {t('common:buttons.delete')}
        </Menu.Item>
      </Menu>
    </div>
  ) : null;

  if (shouldShowInitialLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          tip={t('folders:tree.loading')}
        />
      </div>
    );
  }

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Toolbar {...toolbarProps} />

      <BreadcrumbNav
        breadcrumbs={breadcrumbs}
        onBreadcrumbClick={handleBreadcrumbClick}
      />

      <div className={styles.treeContainer} ref={containerRef}>
        <TreeContainer
          ref={treeRef}
          filteredTree={filteredTree}
          debouncedSearchQuery={debouncedSearchQuery}
          treeProps={treeProps}
          renderNode={renderNode}
          isLoading={false}
          onResetSearch={resetSearch}
        />
      </div>

      {globalContextMenu}

      <CreateFolderModal {...createFolderModalProps} />
      <CreateListModal {...createListModalProps} />

      {moveModal.node && (
        <MoveItemModal
          visible={moveModal.visible}
          itemName={moveModal.node.data.name}
          itemType={moveModal.node.data.type}
          folderTree={effectiveTreeData}
          excludedNodeIds={excludedNodeIds}
          onMove={handleMoveItem}
          onCancel={() => setMoveModal({ visible: false, node: null })}
          isMoving={reduxState.isMoving}
        />
      )}

      {duplicateNode && (
        <MoveItemModal
          visible
          action="duplicate"
          itemName={duplicateNode.data.name}
          itemType="list"
          folderTree={effectiveTreeData}
          excludedNodeIds={NO_EXCLUDED_NODES}
          initialFolderId={getParentFolderId(duplicateNode)}
          onMove={handleDuplicateList}
          onCancel={() => setDuplicateNode(null)}
          isMoving={isDuplicating}
        />
      )}

      {sharedWordsList && (
        <ShareWordsListModal
          wordsList={sharedWordsList}
          hideModal={() => setSharedWordsList(null)}
        />
      )}
    </div>
  );
};

export default React.memo(FolderTreeView);
